const Media = ({ concept, production, video }) => (
  <section id="media" className="container pv6 tc mid-gray">
    <h2 className="heading mb2 purple">Media</h2>
    <h3 className="subheading mb4 pb3">Video &amp; Image Gallery</h3>
    <Gallery concept={concept} production={production} video={video} />
  </section>
)

export default Media

/*
 *
 * General
 * 
 */

import React, { Fragment } from 'react'
import shortid from 'shortid'

/*
 *
 * Gallery
 * 
 */

import Lightbox from 'react-image-lightbox'

import Img from '../components/base/Img'

class Gallery extends React.Component {
  state = {
    activeCategory: `Video`,
    imageLightboxIsOpen: false,
    videoLightboxIsOpen: false,
    photoIndex: null,
    showMobileLayout: true
  }

  componentDidMount = () => {
    // If the browser supports CSS.supports()...
    if (`CSS` in window && `supports` in window.CSS) {
      // ...and CSS grid, use non-mobile layout on large screens
      if (CSS.supports(`display`, `grid`)) {
        // Check on first load
        this.updateLayout()

        // Check whenever window resizes
        window.addEventListener(`resize`, this.updateLayout)
      }
    }
  }

  updateLayout = () => {
    if (window.matchMedia(`(min-width: 48em)`).matches) {
      this.state.showMobileLayout && this.setState({ showMobileLayout: false })
    } else {
      !this.state.showMobileLayout && this.setState({ showMobileLayout: true })
    }
  }

  handleFilterClick = event => {
    if (this.state.activeCategory !== event.target.innerHTML) {
      this.setState({ activeCategory: event.target.innerHTML })
    }
  }

  handleImageClick = index => {
    if (this.state.activeCategory === `Video`) {
      this.setState({ videoLightboxIsOpen: true, photoIndex: index })
    } else {
      this.setState({ imageLightboxIsOpen: true, photoIndex: index })
    }
  }

  handleEnter = ({ event, index }) => event.charCode === 13 && this.handleImageClick(index)

  componentWillUnmount = () => window.removeEventListener(`resize`, this.updateLayout)

  render() {
    const { concept, production, video } = this.props
    const {
      activeCategory,
      imageLightboxIsOpen,
      videoLightboxIsOpen,
      photoIndex,
      showMobileLayout
    } = this.state

    // Filter images by category (for main page and lightbox)
    const activeImages =
      activeCategory === `Video` ? video : activeCategory === `Concept` ? concept : production

    // Set lightbox variables
    let nextIndex, prevIndex
    if (activeImages.length > 1) {
      nextIndex = (photoIndex + 1) % activeImages.length
      prevIndex = (photoIndex + activeImages.length - 1) % activeImages.length
    }

    return (
      <Fragment>
        <Filters category={activeCategory} handleClick={this.handleFilterClick} />

        {/* Image thumbnails */}
        {showMobileLayout ? (
          <Fragment>
            {/* Show first 3 images on mobile */}
            <Images
              images={activeImages.slice(0, 3)}
              category={activeCategory}
              handleImageClick={this.handleImageClick}
              handleEnter={this.handleEnter}
            />

            {/* Show lightbox on mobile (to see more than 3 images) */}
            {activeImages.length > 3 && (
              <button
                type="button"
                onClick={() => this.setState({ lightboxIsOpen: true, photoIndex: 3 })}
                className="dib mt4 pt3 f4 ttu purple animate hover:mid-gray"
              >
                View more {activeCategory === `Video` ? `videos` : `images`}
              </button>
            )}
          </Fragment>
        ) : (
          <Images
            images={activeImages}
            category={activeCategory}
            handleImageClick={this.handleImageClick}
            handleEnter={this.handleEnter}
          />
        )}

        {/* Image Lightbox */}
        {imageLightboxIsOpen && (
          <Lightbox
            mainSrc={activeImages[photoIndex].node.image.childImageSharp.sizes.src}
            nextSrc={
              nextIndex ? activeImages[nextIndex].node.image.childImageSharp.sizes.src : null
            }
            prevSrc={
              prevIndex ? activeImages[prevIndex].node.image.childImageSharp.sizes.src : null
            }
            imageCaption={
              activeCategory === `Concept` ? activeImages[photoIndex].node.alt : null
            }
            onCloseRequest={() => this.setState({ imageLightboxIsOpen: false })}
            onMovePrevRequest={() => this.setState({ photoIndex: prevIndex })}
            onMoveNextRequest={() => this.setState({ photoIndex: nextIndex })}
            enableZoom={false}
            animationOnKeyInput={true}
          />
        )}

        {/* Video Lightbox */}
        {videoLightboxIsOpen && (
          <VideoLightbox
            mainSrc={
              // TODO: move styles to reactModalStyle prop so clicking outside the video will close the modal?
              // See: https://github.com/treyhuffine/lightbox-react#options
              <div className="flex justify-center items-center w-100 h-100">
                <ReactPlayer
                  url={activeImages[photoIndex].node.url}
                  width="100%"
                  height="100%"
                  style={{ display: `flex`, alignItems: `center` }}
                  playing
                />
              </div>
            }
            nextSrc={
              nextIndex ? (
                <div className="flex justify-center items-center w-100 h-100">
                  <ReactPlayer
                    url={activeImages[nextIndex].node.url}
                    width="inherit"
                    height="inherit"
                  />
                </div>
              ) : null
            }
            prevSrc={
              prevIndex ? (
                <div className="flex justify-center items-center w-100 h-100">
                  <ReactPlayer
                    url={activeImages[prevIndex].node.url}
                    width="inherit"
                    height="inherit"
                  />
                </div>
              ) : null
            }
            onCloseRequest={() => this.setState({ videoLightboxIsOpen: false })}
            onMovePrevRequest={() => this.setState({ photoIndex: prevIndex })}
            onMoveNextRequest={() => this.setState({ photoIndex: nextIndex })}
            enableZoom={false}
            animationOnKeyInput={true}
          />
        )}
      </Fragment>
    )
  }
}

/*
 *
 * Videos
 * 
 */

import VideoLightbox from 'lightbox-react'
import ReactPlayer from 'react-player'

const Videos = () => <div />

/*
 *
 * Filters
 * 
 */

const Filters = ({ category, handleClick }) => (
  <div className="mb4">
    <button
      onClick={handleClick}
      className={`filter-btn mb3` + (category === `Video` ? ` active` : ``)}
    >
      Video
    </button>
    <button
      onClick={handleClick}
      className={`filter-btn mb3` + (category === `Production` ? ` active` : ``)}
    >
      Production
    </button>
    <button
      onClick={handleClick}
      className={`filter-btn mb3` + (category === `Concept` ? ` active` : ``)}
    >
      Concept
    </button>
  </div>
)

/*
 *
 * Images
 * 
 */

const Images = ({ images, category, handleImageClick, handleEnter }) => (
  <ul className="media-grid">
    {images.map((item, index) => {
      return (
        <li
          key={shortid.generate()}
          role="button"
          tabIndex="0"
          onClick={() => handleImageClick(index)}
          onKeyPress={event => handleEnter({ event, index })}
          className="group mb3"
        >
          <span className="sr-only">Click or press enter to read full bio</span>
          <div className="relative">
            <Img
              sizes={item.node.image.childImageSharp.sizes}
              alt={item.node.alt}
              critical={true}
              className="aspect-ratio aspect-ratio--1x1"
            />

            {/* Overlay */}
            <p className="flex justify-center items-center absolute fill o-0 group-hover:o-100 bg-black-50 f4 ttu white animate">
              {category === `Video` ? `Watch video` : `View image`}
            </p>
          </div>
        </li>
      )
    })}
  </ul>
)



// WEBPACK FOOTER //
// ./src/sections/Media.js